/**
 * Planned maintenance: the pipeline's maintenance-on / -announce / -off lanes
 * write or delete /maintenance.json in each brand's bucket, next to the SPA.
 *
 *   {"active": boolean, "message"?: string, "starts_at"?: ISO, "until"?: ISO}
 *
 * Anything else - no file (CloudFront answers a missing file with a 404 page),
 * an error status, HTML, bad JSON, no boolean `active` - means no maintenance. Unparseable dates are ignored rather than failing the notice.
 */

export const MAINTENANCE_URL = '/maintenance.json';
/** How often an open page re-reads the maintenance flag. */
export const MAINTENANCE_POLL_MS = 150000;

export interface MaintenanceNotice {
  active: boolean;
  message?: string;
  startsAt?: Date;
  until?: Date;
}

export type MaintenanceView =
  | { kind: 'none' }
  /** The site is closed for maintenance; `overdue` once `until` has passed. */
  | { kind: 'active'; message?: string; until?: Date; overdue: boolean }
  /** A window is announced but has not started: show a banner. */
  | { kind: 'upcoming'; message?: string; startsAt: Date; until?: Date };

const toDate = (v: unknown): Date | undefined => {
  if (typeof v !== 'string' || !v.trim()) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

export function parseMaintenance(raw: unknown): MaintenanceNotice | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.active !== 'boolean') return null;
  const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim() : undefined;
  return { active: o.active, message, startsAt: toDate(o.starts_at), until: toDate(o.until) };
}

/** Reads the flag; never throws. Cache-busted, and no-store so no layer keeps a stale copy. */
export async function fetchMaintenance(url = MAINTENANCE_URL, now = Date.now()): Promise<MaintenanceNotice | null> {
  try {
    const res = await fetch(`${url}?t=${now}`, { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    return parseMaintenance(JSON.parse(await res.text()));
  } catch {
    return null;
  }
}

export function maintenanceView(notice: MaintenanceNotice | null, now: Date): MaintenanceView {
  if (!notice) return { kind: 'none' };
  const { message, startsAt, until } = notice;
  if (notice.active) return { kind: 'active', message, until, overdue: !!until && until <= now };
  if (startsAt && startsAt > now) return { kind: 'upcoming', message, startsAt, until };
  return { kind: 'none' };
}

/** A time in the visitor's own zone (or the one given), e.g. "Thu 16 Oct, 9:30 pm AEDT". */
export function formatLocalTime(d: Date, timeZone?: string): string {
  return d.toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
  });
}
